import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import { ChevronDown, ChevronRight, Plus, FileDown, Eye, Mail, X, DollarSign, Check, Trash2, Bell, BellOff, Send, CreditCard, ExternalLink, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { NumericFormat } from "react-number-format";
import { onlinePaymentsEnabled, useOnlinePayments, emailIntro, viewLabel } from "@/lib/onlinePayments";
import { getWOInvoices, createInvoice, updateInvoice, updateInvoiceStatus, deleteInvoice, sendManualReminder, togglePauseReminders, getInvoiceReminders, createPaymentLink } from "@/lib/api";
import { toast } from "sonner";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { getEmailDocMode, docFilename, withDocName } from "@/components/common/EmailDocMode";
import { beginEscapeGuard, endEscapeGuard } from "@/lib/escapeGuard";
import { consumeFocusTarget, scrollToTestId, FOCUS_INVOICE } from "@/lib/deepLinkFocus";
import { COMPANY_INFO } from "@/lib/companyInfo";
import EmailHistoryPanel from "./EmailHistoryPanel";
import FoundationARPushButton from "@/components/integrations/FoundationARPushButton";
import { TaxHiddenBadge } from "@/components/common/TaxHiddenBadge";
import { TaxOverrideInline, useCanOverrideTax } from "@/components/common/TaxOverrideDialog";
import { TaxOverrideBadge } from "@/components/common/TaxOverrideBadge";
import { btn } from "@/components/common/btn";
import { shortDate } from "@/lib/format";

const round2 = (v) => Math.round(((v || 0) + Number.EPSILON) * 100) / 100;
// Invoice notes are a plain string server-side, but older responses (and
// any WO-style journal shape) can arrive as [{date, text}] — always
// render/send text, never an object (iter289 crash).
const notesToText = (n) => {
  if (Array.isArray(n)) {
    return n.map(x => (typeof x === "object" && x !== null ? [x.date, x.text].filter(Boolean).join(": ") : String(x))).join("\n");
  }
  return typeof n === "string" ? n : "";
};
const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
};
const STATUS_LABELS = { draft: "Draft", sent: "Sent", paid: "Paid", partial: "Partial" };

// Build customer-facing invoice rows from WO line items
// Each line stays as-is (matching WO), just strip internal parentheticals
function cleanInvoiceDesc(desc) {
  let cleaned = (desc || "").replace(/\s*\[.*?\]\s*/g, "").replace(/\s*\(.*?\)\s*/g, "").trim();
  // Handle "X - X" duplication
  const dashParts = cleaned.split(" - ");
  if (dashParts.length === 2 && dashParts[0].trim().toLowerCase() === dashParts[1].trim().toLowerCase()) {
    cleaned = dashParts[0].trim();
  }
  // Handle "Scope: Description" — strip scope prefix
  const colonIdx = cleaned.indexOf(": ");
  if (colonIdx > 0) {
    const detail = cleaned.substring(colonIdx + 2).trim();
    if (detail.length > 0) cleaned = detail;
  }
  return cleaned;
}

// Material-only slice of a raw WO line — the only part that is taxable.
const isTaxableRawLine = (item) => {
  if (item?.taxable === false) return false;
  const s = String(item?.cost_type || "").trim().toLowerCase();
  return s === "2" || s === "material";
};

function consolidateForInvoice(items) {
  const grouped = {};
  const result = [];
  (items || []).forEach(item => {
    const cleanDesc = cleanInvoiceDesc(item.description);
    const pn = (item.part_number || "").trim();
    const key = pn || cleanDesc;
    const ct = String(item.cost_type || "").toLowerCase();
    const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
    const taxableSlice = isTaxableRawLine(item) ? lineTotal : 0;
    const matQty = (ct === "2" || ct === "material") ? (item.quantity || 1) : null;
    if (key && grouped[key]) {
      // #251 (iter168): accumulate BOTH the total AND the material qty
      // so duplicates of the same SKU/description don't silently lose
      // count. Recompute unit_price from the running totals so the row
      // math reconciles regardless of how many sources contributed.
      grouped[key].total += lineTotal;
      grouped[key].taxable_amount = round2(grouped[key].taxable_amount + taxableSlice);
      if (matQty !== null) grouped[key].quantity += matQty;
      grouped[key].unit_price = grouped[key].quantity > 0 ? grouped[key].total / grouped[key].quantity : grouped[key].total;
    } else if (key) {
      const qty = matQty !== null ? matQty : 1;
      const row = {
        id: item.id || Date.now().toString() + Math.random().toString(36).slice(2),
        description: cleanDesc,
        part_number: pn,
        cost_type: item.cost_type || "",  // #249 (iter168): preserve cost_type so the tax-on-labor toggle works
        quantity: qty,
        unit_cost: 0,
        unit_price: lineTotal / qty || lineTotal,
        total: lineTotal,
        // Merged rows mix material with labor/freight — pin the taxable
        // (material) slice so the invoice taxes exactly what the WO did.
        taxable_amount: round2(taxableSlice),
      };
      grouped[key] = row;
      result.push(row);
    } else {
      result.push({
        id: item.id || Date.now().toString() + Math.random().toString(36).slice(2),
        description: cleanDesc,
        part_number: pn,
        cost_type: item.cost_type || "",
        quantity: item.quantity || 1,
        unit_cost: item.unit_cost || 0,
        unit_price: item.unit_price || 0,
        total: lineTotal,
      });
    }
  });
  return result;
}

// Break out labor: returns one row per item with material_total and labor_total columns
// Material includes material cost, freight, equipment, disposal. Labor is separate.
function breakOutLabor(items) {
  const grouped = {};
  const result = [];

  (items || []).forEach(item => {
    const ct = String(item.cost_type || "").toLowerCase();
    const cleanDesc = cleanInvoiceDesc(item.description);
    const pn = (item.part_number || "").trim();
    const key = pn || cleanDesc;
    const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
    const isLabor = ct === "1" || ct === "labor";
    const taxableSlice = isTaxableRawLine(item) ? lineTotal : 0;
    const matQty = (ct === "2" || ct === "material") ? (item.quantity || 1) : null;

    if (key && grouped[key]) {
      if (isLabor) {
        grouped[key].labor_total += lineTotal;
      } else {
        grouped[key].material_total += lineTotal;
      }
      grouped[key].taxable_amount = round2(grouped[key].taxable_amount + taxableSlice);
      if (matQty !== null) grouped[key].quantity = matQty;
      grouped[key].total = grouped[key].material_total + grouped[key].labor_total;
    } else if (key) {
      const qty = matQty !== null ? matQty : 1;
      const row = {
        id: item.id || Date.now().toString() + Math.random().toString(36).slice(2),
        description: cleanDesc,
        part_number: pn,
        cost_type: isLabor ? "labor" : (item.cost_type || ""),
        quantity: qty,
        material_total: isLabor ? 0 : lineTotal,
        labor_total: isLabor ? lineTotal : 0,
        total: lineTotal,
        taxable_amount: round2(taxableSlice),
      };
      grouped[key] = row;
      result.push(row);
    } else {
      // #252 (iter168): items with NO part_number AND blank description
      // were previously dropped silently — wiping line revenue from the
      // invoice. Push them through as standalone rows with a synthetic
      // unique key so the customer is billed correctly.
      result.push({
        id: item.id || Date.now().toString() + Math.random().toString(36).slice(2),
        description: cleanDesc,
        part_number: pn,
        cost_type: isLabor ? "labor" : (item.cost_type || ""),
        quantity: item.quantity || 1,
        material_total: isLabor ? 0 : lineTotal,
        labor_total: isLabor ? lineTotal : 0,
        total: lineTotal,
      });
    }
  });

  // Recalc unit_price for display
  result.forEach(r => { r.unit_price = r.quantity > 0 ? r.total / r.quantity : r.total; });
  return result;
}

const InvoiceSection = forwardRef(function InvoiceSection({
  woId, woNumber, woTitle, clientName, clientId, siteId, siteName, projectManagerId, projectManagerName,
  lineItems, changeOrders, taxRate, woTaxOverride, hideTax, jobsiteAddress, contactName, contactEmail, contactPhone,
  customerNumber,
}, ref) {
  const [invoices, setInvoices] = useState([]);
  const [expandedInv, setExpandedInv] = useState(null);
  // iter273 — Cmd+K deep-link: expand the invoice the user picked.
  useEffect(() => {
    if (!invoices.length) return;
    const target = consumeFocusTarget(FOCUS_INVOICE);
    if (!target || !invoices.some((i) => i.id === target)) return;
    setExpandedInv(target);
    scrollToTestId(`invoice-card-${target}`);
  }, [invoices]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (woId) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [woId]);

  // T&M — refresh when the T&M Charges panel generates a new invoice.
  useEffect(() => {
    const onRefresh = () => { if (woId) load(); };
    window.addEventListener("wo:invoices-refresh", onRefresh);
    return () => window.removeEventListener("wo:invoices-refresh", onRefresh);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [woId]);

  useImperativeHandle(ref, () => ({ refresh: load }));

  const load = async () => {
    try {
      const res = await getWOInvoices(woId);
      setInvoices(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const summary = useMemo(() => {
    const total = invoices.reduce((s, inv) => s + (inv.total || 0), 0);
    const paid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);
    const draft = invoices.filter(i => i.status === "draft").length;
    const sent = invoices.filter(i => i.status === "sent").length;
    return { count: invoices.length, total, paid, unpaid: total - paid, draft, sent };
  }, [invoices]);

  // Gather all WO + approved CO line items
  // Compute which items have already been invoiced
  const invoicedDescriptions = useMemo(() => {
    const set = new Set();
    (invoices || []).forEach(inv => {
      (inv.line_items || []).forEach(li => {
        const key = (li.part_number || "").trim() || cleanInvoiceDesc(li.description);
        if (key) set.add(key.toLowerCase());
      });
    });
    return set;
  }, [invoices]);

  const allLineItems = useMemo(() => {
    const woItems = [...(lineItems || [])];
    (changeOrders || []).filter(co => co.status === "approved").forEach(co => {
      (co.line_items || []).forEach(li => woItems.push(li));
    });
    return woItems;
  }, [lineItems, changeOrders]);

  // Raw unbilled line items (cost_type preserved — needed for labor breakout).
  // We filter out any raw items whose key (part_number or cleaned description)
  // has already been billed on a prior invoice.
  const unbilledLineItems = useMemo(() => {
    return (allLineItems || []).filter(item => {
      const key = (item.part_number || "").trim() || cleanInvoiceDesc(item.description || "");
      return key && !invoicedDescriptions.has(key.toLowerCase());
    });
  }, [allLineItems, invoicedDescriptions]);

  if (loading) return null;

  return (
    <div className="space-y-2" data-testid="wo-invoices-section">
      {/* Invoices tab (UI sweep batch 4) — the tab carries the name; toolbar: "+ Invoice" left, counts right. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="invoices-toolbar">
        <button type="button" className={btn("write")} onClick={() => {
          if (!customerNumber) { toast.error("Invoicing unavailable — Customer # required"); return; }
          setShowCreate(true);
        }} data-testid="btn-create-invoice" disabled={!customerNumber}>
          <Plus className="w-3 h-3" /> Invoice
        </button>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span className="num">{summary.count} invoice{summary.count !== 1 ? "s" : ""}</span>
          {summary.count > 0 && <span className="num">{fmt(summary.total)}</span>}
          {summary.unpaid > 0 && <span className="text-amber-600 font-medium num">{fmt(summary.unpaid)} unpaid</span>}
        </div>
      </div>

        <div className="space-y-2">
          {!customerNumber && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2" data-testid="invoice-cust-num-gate">
              <strong>Invoicing unavailable — Customer # required.</strong> Add or request a Customer # on the client record before creating invoices for this job.
            </div>
          )}

          {showCreate && (
            <CreateInvoiceForm
              woId={woId}
              woNumber={woNumber}
              woTitle={woTitle}
              clientId={clientId}
              siteId={siteId}
              siteName={siteName}
              clientName={clientName}
              contactName={contactName}
              contactEmail={contactEmail}
              contactPhone={contactPhone}
              customerNumber={customerNumber}
              projectManagerId={projectManagerId}
              projectManagerName={projectManagerName}
              allLineItems={unbilledLineItems}
              taxRate={taxRate}
              woTaxOverride={woTaxOverride}
              hideTax={hideTax}
              jobsiteAddress={jobsiteAddress}
              onSaved={(newId) => { setShowCreate(false); load(); if (newId) setExpandedInv(newId); }}
              onCancel={() => setShowCreate(false)}
            />
          )}

          {invoices.length === 0 && !showCreate && (
            <p className="text-xs text-slate-400 italic py-2">No invoices yet. Create one from the work order line items.</p>
          )}

          {invoices.map(inv => (
            <InvoiceCard
              key={inv.id}
              invoice={inv}
              expanded={expandedInv === inv.id}
              onToggle={() => setExpandedInv(expandedInv === inv.id ? null : inv.id)}
              onSaved={load}
              woNumber={woNumber}
              clientName={clientName}
              jobsiteAddress={jobsiteAddress}
            />
          ))}
        </div>
    </div>
  );
});

export default InvoiceSection;

/* ─── Create Invoice Form ─── */
function CreateInvoiceForm({ woId, woNumber, woTitle, clientId, siteId, siteName, clientName, contactName, contactEmail, contactPhone, customerNumber, projectManagerId, projectManagerName, allLineItems, taxRate, woTaxOverride, hideTax, jobsiteAddress, onSaved, onCancel }) {
  const [laborBreakout, setLaborBreakout] = useState(false);
  const [saving, setSaving] = useState(false);

  // Build invoice line items from WO data
  const invoiceItems = useMemo(() => {
    if (laborBreakout) {
      return breakOutLabor(allLineItems);
    }
    return consolidateForInvoice(allLineItems);
  }, [allLineItems, laborBreakout]);

  const [selectedItems, setSelectedItems] = useState([]);

  // Init selection when items change
  useEffect(() => {
    setSelectedItems(invoiceItems.map(() => true));
    // Only re-init when item count changes (not on every reference
    // identity flip).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceItems.length, laborBreakout]);

  const selectedLineItems = invoiceItems.filter((_, i) => selectedItems[i]);
  const invSubtotal = selectedLineItems.reduce((s, li) => s + (li.total || 0), 0);
  // Labor is non-taxable in most US jurisdictions — exclude labor line
  // items from the tax base (matches backend invoices.py:596 and the
  // proposalUtils.calcProposalTotals math).
  // #249 (iter168): labor exclusion must recognise BOTH the legacy
  // "labor" string and the canonical numeric-string code "1" — WO
  // line items use "1" (per COST_TYPE_MAP) and the prior check missed
  // every one of them, so labor was always taxed on the invoice.
  // ONE shared tax rule with the Work Order: MATERIAL ONLY is taxable.
  // Labor, equipment, freight, travel, disposal and misc are excluded,
  // and a line can opt out with taxable === false. This is what made the
  // invoice preview disagree with the WO before.
  const isTaxableInvLine = (li) => {
    if (li?.taxable === false) return false;
    const s = String(li?.cost_type || "").trim().toLowerCase();
    return s === "2" || s === "material";
  };
  // A merged row pins its material-only slice in `taxable_amount`
  // (mirrors _recompute_invoice_totals) — that is what the WO taxed.
  const invTaxableBase = selectedLineItems.reduce((s, li) => {
    if (li?.taxable_amount != null) return s + Math.min(li.taxable_amount, li.total || 0);
    return s + (isTaxableInvLine(li) ? (li.total || 0) : 0);
  }, 0);
  // iter310 — jurisdiction rate always; hiding tax is display-only.
  // A manual tax on the WO (carried from the proposal) owns the rate.
  const effectiveTaxRate = woTaxOverride?.type === "rate" ? (parseFloat(woTaxOverride.rate) || 0) : (taxRate || 0);
  const allSelected = selectedLineItems.length === invoiceItems.length;
  const invTax = woTaxOverride?.type === "amount" && allSelected
    ? round2(parseFloat(woTaxOverride.amount) || 0)
    : round2(invTaxableBase * (effectiveTaxRate / 100));
  const invTotal = round2(invSubtotal + invTax);

  const toggleItem = (idx) => {
    setSelectedItems(prev => { const n = [...prev]; n[idx] = !n[idx]; return n; });
  };

  const handleCreate = async ({ emailAfter = false } = {}) => {
    if (!customerNumber || !String(customerNumber).trim()) {
      toast.error("Invoicing unavailable — Customer # required. Please add or request a Customer # on the client record before invoicing.");
      return;
    }
    if (selectedLineItems.length === 0) { toast.error("Select at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        work_order_id: woId,
        client_id: clientId,
        site_id: siteId,
        site_name: siteName || "",
        project_title: woTitle || "",
        project_manager_id: projectManagerId || "",
        project_manager_name: projectManagerName || "",
        customer_name: clientName || "",
        customer_contact: contactName || "",
        customer_email: contactEmail || "",
        customer_phone: contactPhone || "",
        project_address: jobsiteAddress || "",
        wo_number: woNumber || "",
        line_items: selectedLineItems.map(li => ({
          description: li.description,
          part_number: li.part_number || "",
          cost_type: li.cost_type || "",
          quantity: li.quantity || 1,
          unit_cost: li.unit_cost || 0,
          unit_price: li.unit_price || 0,
          total: li.total || 0,
          taxable: li.taxable !== false,
          taxable_amount: li.taxable_amount != null ? round2(li.taxable_amount) : null,
        })),
        subtotal: invSubtotal,
        hide_tax: !!hideTax,
        tax_rate: effectiveTaxRate,
        tax_amount: invTax,
        total: invTotal,
        notes: "",
        is_final: false,
        // Labor breakout on the create form doubles as the PDF
        // breakdown flag for the invoice it produces.
        show_breakdown: !!laborBreakout,
      };
      const res = await createInvoice(woId, payload);
      const created = res?.data || {};
      // The server always creates as draft; flip it to sent through the
      // status endpoint when the user chose "Invoice" (save + email).
      if (emailAfter && created.id) {
        try { await updateInvoiceStatus(created.id, "sent"); } catch { /* status stays draft */ }
      }
      toast.success(emailAfter ? "Invoice saved & opening email" : "Invoice saved");

      if (emailAfter && created.id) {
        // Backend auto-created the Square payment link + short_code + embedded the Pay Now
        // button into the invoice PDF. Email carries ONE link to the PDF — customer clicks
        // "Pay Now" on the PDF itself for secure CC or ACH.
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const shortCode = created.short_code || "";
        const invNum = created.invoice_number || "";
        const docName = docFilename("Invoice", invNum);
        const attach = getEmailDocMode() === "attach";
        if (attach) downloadPdfUrl(`/invoices/${created.id}/pdf?download=1`, { suggestedName: docName, label: "invoice" }).catch(() => {});
        const pdfUrl = attach ? `(${docName} attached)` : withDocName(shortCode ? `${baseUrl}/api/r/i/${shortCode}` : `${baseUrl}/api/invoices/${created.id}/pdf`, docName);
        const email = contactEmail || "";
        const payOn = await onlinePaymentsEnabled();
        const subject = encodeURIComponent(`Invoice ${invNum} from ${COMPANY_INFO.name}`);
        const body = encodeURIComponent(
          `Hi ${contactName || "there"},\n\n` +
          `${emailIntro("invoice", payOn)}\n\n` +
          `Invoice ${invNum}\n` +
          `${viewLabel(payOn)}: ${pdfUrl}\n\n` +
          `Amount Due: ${fmt(invTotal)}\n` +
          `Payment Terms: Net 45 days\n\n` +
          `Please remit payment within 30 days of the invoice date. Online payment is the fastest option — checks and ACH are also welcome.\n\n` +
          `Thank you,\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
        );
        // Synchronous navigation to mailto — browser hands off to OS mail handler without leaving the page blank
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      }
      onSaved(created.id);
    } catch { toast.error("Failed to save invoice"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="create-invoice-form">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold text-slate-700">New invoice from WO {woNumber}</h5>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600" data-testid="cancel-create-invoice"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="labor-breakout-toggle">
          <input type="checkbox" checked={laborBreakout} onChange={(e) => setLaborBreakout(e.target.checked)} className="h-3.5 w-3.5 rounded accent-blue-600" />
          <span className="font-medium">Labor breakout</span>
          <span className="text-slate-400">(separate labor from material)</span>
        </label>
      </div>

      <div className="text-[10px] text-slate-400 mb-1">Select items to include on this invoice:</div>

      <div className="border rounded overflow-x-auto bg-white">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-100 text-[11px] text-slate-500">
              <th className="py-1.5 px-2 w-8">
                <input type="checkbox" checked={selectedItems.every(Boolean)} onChange={(e) => setSelectedItems(invoiceItems.map(() => e.target.checked))} className="h-3 w-3 accent-blue-600" />
              </th>
              <th className="py-1.5 px-2 text-left w-24">Part #</th>
              <th className="py-1.5 px-2 text-left">Description</th>
              <th className="py-1.5 px-2 text-right w-16">Qty</th>
              {laborBreakout && <th className="py-1.5 px-2 text-right w-24">Material</th>}
              {laborBreakout && <th className="py-1.5 px-2 text-right w-24">Labor</th>}
              <th className="py-1.5 px-2 text-right w-24">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoiceItems.map((li, idx) => (
              <tr key={li.id || idx} className={`border-b border-slate-50 ${selectedItems[idx] ? "" : "opacity-40"}`}>
                <td className="py-1.5 px-2">
                  <input type="checkbox" checked={selectedItems[idx] || false} onChange={() => toggleItem(idx)} className="h-3 w-3 accent-blue-600" data-testid={`inv-item-check-${idx}`} />
                </td>
                <td className="py-1.5 px-2 font-mono text-slate-600">{li.part_number || "—"}</td>
                <td className="py-1.5 px-2 text-slate-700">{li.description}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">{li.quantity}</td>
                {laborBreakout && <td className="py-1.5 px-2 text-right tabular-nums">{fmt(li.material_total || 0)}</td>}
                {laborBreakout && <td className="py-1.5 px-2 text-right tabular-nums">{fmt(li.labor_total || 0)}</td>}
                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{fmt(li.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="w-56 space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums" data-testid="invoice-preview-subtotal">{fmt(invSubtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Tax ({effectiveTaxRate}%)</span><span className="tabular-nums" data-testid="invoice-preview-tax">{fmt(invTax)}</span></div>
          {hideTax && (
            <div className="flex justify-end pt-0.5">
              <TaxHiddenBadge taxAmount={invTax} taxRate={effectiveTaxRate} testId="invoice-create-tax-hidden-badge" />
            </div>
          )}
          <div className="flex justify-between font-bold text-sm border-t pt-1"><span>Total</span><span className="tabular-nums" data-testid="invoice-preview-total">{fmt(invTotal)}</span></div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" size="sm" className={btn("nav")} onClick={onCancel}>Cancel</Button>
        <Button type="button" size="sm" className={btn("write")} onClick={() => handleCreate({ emailAfter: false })} disabled={saving} data-testid="btn-save-invoice">
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
        <Button type="button" size="sm" className={btn("data")} onClick={() => handleCreate({ emailAfter: true })} disabled={saving} data-testid="btn-email-invoice-new">
          <Mail className="w-3 h-3 mr-1" /> Save & email
        </Button>
      </div>
    </div>
  );
}

/* ─── Invoice Card ─── */
function InvoiceCard({ invoice, expanded, onToggle, onSaved, woNumber, clientName, jobsiteAddress }) {
  const payOn = useOnlinePayments();
  const [local, setLocal] = useState({ ...invoice });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const canOverrideTax = useCanOverrideTax();
  const invoiceAutoSaveRef = useRef(null);
  const isInvFirstLoad = useRef(true);

  useEffect(() => {
    // Don't clobber in-flight edits when the parent reloads the list
    // (the autosave's own onSaved() triggers exactly that).
    if (dirty) return;
    setLocal({ ...invoice });
    isInvFirstLoad.current = true;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [invoice]);

  // Auto-save invoice (debounced)
  useEffect(() => {
    if (isInvFirstLoad.current) { isInvFirstLoad.current = false; return; }
    if (!dirty || !invoice.id) return;
    if (invoiceAutoSaveRef.current) clearTimeout(invoiceAutoSaveRef.current);
    invoiceAutoSaveRef.current = setTimeout(async () => {
      try {
        const payload = { line_items: local.line_items, subtotal: local.subtotal, tax_rate: local.tax_rate, tax_rate_manual: !!local.tax_rate_manual, tax_amount: local.tax_amount, total: local.total, notes: notesToText(local.notes), customer_po_number: local.customer_po_number, invoice_date: local.invoice_date || undefined, is_final: local.is_final, show_breakdown: !!local.show_breakdown };
        await updateInvoice(invoice.id, payload);
        setDirty(false);
        // No onSaved() here — reloading the parent list mid-edit is what
        // made the WO page jump back to the top while typing.
      } catch (e) {
        // Never swallow autosave failures — a 422 here used to silently
        // drop every edit in the payload (iter289).
        console.error("Invoice autosave failed", e?.response?.data || e);
        toast.error(e?.response?.data?.detail ? `Autosave failed: ${typeof e.response.data.detail === "string" ? e.response.data.detail : "invalid data"}` : "Invoice autosave failed — use Save to retry");
      }
    }, 1500);
    return () => { if (invoiceAutoSaveRef.current) clearTimeout(invoiceAutoSaveRef.current); };
    // Debounced autosave — `invoice.id` is captured at mount (the
    // invoice prop is stable per row) and `onSaved` is the parent's
    // callback. Adding them would re-arm the timer on every parent
    // re-render and break the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, dirty]);

  const lineSubtotal = (local.line_items || []).reduce((s, li) => s + (li.total || 0), 0);

  const updateField = (field, value) => {
    setLocal(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  // Hold Escape while the tax dialog is open — Radix listens on document
  // in the capture phase, so the WO sheet has to be told not to dismiss.
  useEffect(() => {
    if (!taxDialogOpen) return undefined;
    beginEscapeGuard();
    return endEscapeGuard;
  }, [taxDialogOpen]);

  // iter306 — the server owns the override; take its recomputed doc as-is.
  const applyTaxOverrideResult = (doc) => {
    if (!doc) return;
    setLocal(prev => ({ ...prev, ...doc, notes: prev.notes }));
    setDirty(false);
    onSaved?.();
  };

  // ONE shared tax rule with the Work Order: MATERIAL ONLY is taxable.
  // A line can opt out with taxable === false.
  const isTaxableLine = (li) => {
    if (li?.taxable === false) return false;
    const s = String(li?.cost_type || "").trim().toLowerCase();
    return s === "2" || s === "material";
  };

  const recalcTotals = (items, taxR, override, unified = local.tax_override) => {
    const sub = items.reduce((s, li) => s + (li.total || 0), 0);
    // Mirror the server: a pinned taxable_amount (clamped to the row
    // total) wins over the cost_type rule.
    const taxBase = items.reduce((s, li) => {
      if (li?.taxable_amount != null) return s + Math.min(li.taxable_amount, li.total || 0);
      return s + (isTaxableLine(li) ? (li.total || 0) : 0);
    }, 0);
    const computed = round2(taxBase * ((taxR || 0) / 100));
    let tax = override == null ? computed : round2(override);
    if (unified?.type === "rate") tax = round2(taxBase * ((parseFloat(unified.rate) || 0) / 100));
    else if (unified?.type === "amount") tax = round2(parseFloat(unified.amount) || 0);
    return { subtotal: sub, taxable_subtotal: round2(taxBase), tax_amount: tax, total: round2(sub + tax) };
  };

  const updateLineItem = (idx, field, value) => {
    const items = [...(local.line_items || [])];
    items[idx] = { ...items[idx], [field]: value };
    if (field === "quantity" || field === "unit_price") {
      const prevTotal = round2(items[idx].total || 0);
      const nextTotal = round2((items[idx].quantity || 0) * (items[idx].unit_price || 0));
      items[idx].total = nextTotal;
      // A merged roll-up row carries a pinned taxable slice (material
      // portion). Scale it with the row so re-pricing keeps the same
      // material/labor split the WO taxed — dropping it would re-tax
      // the labor half and put the invoice back out of sync.
      const pin = items[idx].taxable_amount;
      if (pin != null) {
        items[idx].taxable_amount = prevTotal > 0
          ? Math.min(nextTotal, round2(pin * (nextTotal / prevTotal)))
          : nextTotal;
      }
    }
    // Re-categorising the row invalidates the split entirely.
    if (field === "cost_type") delete items[idx].taxable_amount;
    const totals = recalcTotals(items, local.tax_rate, local.tax_amount_override);
    setLocal(prev => ({ ...prev, line_items: items, ...totals }));
    setDirty(true);
  };

  const removeLineItem = (idx) => {
    const items = (local.line_items || []).filter((_, i) => i !== idx);
    const totals = recalcTotals(items, local.tax_rate, local.tax_amount_override);
    setLocal(prev => ({ ...prev, line_items: items, ...totals }));
    setDirty(true);
  };

  const addBlankItem = () => {
    const items = [...(local.line_items || []), { id: Date.now().toString(), description: "", part_number: "", cost_type: "", quantity: 1, unit_price: 0, total: 0 }];
    setLocal(prev => ({ ...prev, line_items: items }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        line_items: local.line_items,
        subtotal: local.subtotal,
        tax_rate: local.tax_rate,
        tax_rate_manual: !!local.tax_rate_manual,
        tax_amount: local.tax_amount,
        total: local.total,
        notes: notesToText(local.notes),
        customer_po_number: local.customer_po_number,
        invoice_date: local.invoice_date || undefined,
        is_final: local.is_final,
        show_breakdown: !!local.show_breakdown,
      };
      await updateInvoice(invoice.id, payload);
      toast.success("Invoice saved");
      setDirty(false);
      onSaved();
    } catch { toast.error("Failed to save invoice"); }
    finally { setSaving(false); }
  };

  // Status moves through PATCH /invoices/{id}/status — the generic PUT
  // drops `status` / `sent_at` / `paid_at` on purpose, so the old
  // updateInvoice() call silently did nothing.
  const handleStatusChange = async (newStatus) => {
    try {
      const res = await updateInvoiceStatus(invoice.id, newStatus);
      setLocal(prev => ({ ...prev, ...(res?.data || {}) }));
      toast.success(`Invoice ${STATUS_LABELS[newStatus] || newStatus}`);
      onSaved();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
      return false;
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await deleteInvoice(invoice.id);
      toast.success("Invoice deleted");
      onSaved();
    } catch { toast.error("Failed to delete"); }
  };

  const generatePDF = () => {
    downloadPdfUrl(`/invoices/${local.id}/pdf?download=1`, {
      suggestedName: `ABS_Invoice_${local.invoice_number || local.id}.pdf`,
      label: "invoice",
    });
  };

  const handleEmail = async () => {
    // Build everything synchronously from cached data — no awaits before mailto.
    // The invoice PDF already has a "Pay Now" button baked in (green button at the bottom
    // of the PDF, same pattern as Parts Counter quotes). Email carries ONE link.
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const shortCode = local.short_code || "";
    const docName = docFilename("Invoice", local.invoice_number);
    const attach = getEmailDocMode() === "attach";
    if (attach) downloadPdfUrl(`/invoices/${invoice.id}/pdf?download=1`, { suggestedName: docName, label: "invoice" }).catch(() => {});
    const pdfUrl = attach ? `(${docName} attached)` : withDocName(shortCode ? `${baseUrl}/api/r/i/${shortCode}` : `${baseUrl}/api/invoices/${invoice.id}/pdf`, docName);
    const email = local.customer_email || local.billing_contact_email || "";
    const contact = local.customer_contact || local.billing_contact_name || "there";
    const subject = encodeURIComponent(`Invoice ${local.invoice_number} from ${COMPANY_INFO.name}`);
    const body = encodeURIComponent(
      `Hi ${contact},\n\n` +
      `${emailIntro("invoice", payOn)}\n\n` +
      `Invoice ${local.invoice_number}\n` +
      `${viewLabel(payOn)}: ${pdfUrl}\n\n` +
      `Amount Due: ${fmt(local.total)}\n` +
      `Payment Terms: Net 45 days\n\n` +
      (payOn
        ? `Please remit payment within 30 days of the invoice date. Online payment is the fastest option — checks and ACH are also welcome.\n\n`
        : `Please remit payment within 30 days of the invoice date — checks and ACH are welcome.\n\n`) +
      `Thank you,\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    // Background: auto-mark as sent + create payment link if missing
    setTimeout(async () => {
      if (payOn && !local.square_payment_url) {
        try {
          const res = await createPaymentLink(invoice.id);
          if (res?.data?.payment_url) setLocal(prev => ({ ...prev, square_payment_url: res.data.payment_url }));
        } catch {}
      }
      if (local.status !== "sent" && local.status !== "paid" && local.status !== "void") {
        await handleStatusChange("sent");
      }
    }, 100);
  };

  // Reminder handlers
  const [reminderLog, setReminderLog] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);

  const loadReminders = async () => {
    setLoadingReminders(true);
    try {
      const res = await getInvoiceReminders(invoice.id);
      setReminderLog(res.data || []);
    } catch { /* silent */ }
    finally { setLoadingReminders(false); }
  };

  useEffect(() => { if (expanded) loadReminders(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [expanded]);

  const handleSendReminder = async () => {
    try {
      await sendManualReminder(invoice.id, {
        tier: (local.last_reminder_tier || 0) + 1,
        tier_label: "Manual Reminder",
        recipient_email: local.customer_email || "",
        recipient_name: local.customer_contact || "",
        subject: `Payment Reminder — Invoice ${local.invoice_number}`,
        body_preview: `Reminder for Invoice ${local.invoice_number}, Amount: ${fmt(local.total)}`,
      });
      toast.success("Reminder logged");
      loadReminders();
      onSaved();
    } catch { toast.error("Failed to send reminder"); }
  };

  const handleTogglePause = async () => {
    const newPaused = !local.reminders_paused;
    try {
      await togglePauseReminders(invoice.id, newPaused);
      setLocal(prev => ({ ...prev, reminders_paused: newPaused }));
      toast.success(newPaused ? "Reminders paused" : "Reminders resumed");
      onSaved();
    } catch { toast.error("Failed to update"); }
  };


  const isPartial = local.status === "sent" && (local.amount_paid || 0) > 0.005 && (local.balance ?? 1) > 0.005;
  const shownStatus = isPartial ? "partial" : local.status;
  const statusBadge = STATUS_COLORS[shownStatus] || STATUS_COLORS.draft;
  const headerDesc = (local.line_items || [])[0]?.description || "Invoice";

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`invoice-card-${invoice.id}`}>
      <button type="button" className="flex items-center gap-2 w-full text-left px-3 py-2 hover:bg-slate-50" onClick={onToggle} data-testid={`invoice-toggle-${invoice.id}`}>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
        <span className="font-mono text-xs font-semibold text-slate-700">{local.invoice_number}</span>
        <span className="text-xs text-slate-500 truncate flex-1">{headerDesc}</span>
        <Badge className={`text-[10px] ${statusBadge}`} data-testid={`invoice-status-${invoice.id}`}
          title={local.foundation_paid_sync ? `Foundation shows ${fmt(local.foundation_paid_sync.paid)} of ${fmt(local.foundation_paid_sync.invoice_amount)} received` : undefined}>
          {STATUS_LABELS[shownStatus] || local.status}{isPartial ? ` · ${fmt(local.amount_paid)} paid` : ""}
        </Badge>
        <span className="text-xs font-semibold tabular-nums">{fmt(local.total)}</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t bg-white">
          {/* Line Items */}
          <div className="border rounded overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-[11px] text-slate-500">
                  <th className="py-1.5 px-2 text-left w-20">Part #</th>
                  <th className="py-1.5 px-2 text-left">Description</th>
                  <th className="py-1.5 px-2 text-right w-14">Qty</th>
                  <th className="py-1.5 px-2 text-right w-24">Unit Price</th>
                  <th className="py-1.5 px-2 text-right w-24">Total</th>
                  {local.status === "draft" && <th className="w-8" />}
                </tr>
              </thead>
              <tbody>
                {(local.line_items || []).map((li, idx) => (
                  <tr key={li.id || idx} className="border-b border-slate-50">
                    <td className="py-1 px-2">
                      {local.status === "draft" ? (
                        <Input value={li.part_number || ""} onChange={(e) => updateLineItem(idx, "part_number", e.target.value)} className="h-6 text-xs font-mono w-full" data-testid={`inv-li-pn-${invoice.id}-${idx}`} />
                      ) : (
                        <span className="font-mono text-slate-600">{li.part_number || "—"}</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {local.status === "draft" ? (
                        <Input value={li.description || ""} onChange={(e) => updateLineItem(idx, "description", e.target.value)} className="h-6 text-xs w-full" data-testid={`inv-li-desc-${invoice.id}-${idx}`} />
                      ) : (
                        <span className="text-slate-700">{li.description}</span>
                      )}
                    </td>
                    <td className="py-1 px-2 text-right">
                      {local.status === "draft" ? (
                        <Input type="number" value={li.quantity} onChange={(e) => updateLineItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="h-6 text-xs w-14 text-right" data-testid={`inv-li-qty-${invoice.id}-${idx}`} />
                      ) : (
                        <span className="tabular-nums">{li.quantity}</span>
                      )}
                    </td>
                    <td className="py-1 px-2 text-right">
                      {local.status === "draft" ? (
                        <NumericFormat value={li.unit_price || ""} onValueChange={(v) => updateLineItem(idx, "unit_price", v.floatValue || 0)}
                          thousandSeparator="," prefix="$" decimalScale={2} allowNegative={false}
                          className="flex h-6 w-24 rounded-md border bg-white px-2 py-1 text-xs text-right shadow-none ring-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-300"
                          data-testid={`inv-li-price-${invoice.id}-${idx}`} />
                      ) : (
                        <span className="tabular-nums">{fmt(li.unit_price)}</span>
                      )}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums font-medium">{fmt(li.total)}</td>
                    {local.status === "draft" && (
                      <td className="py-1 px-1">
                        <button type="button" onClick={() => removeLineItem(idx)} className="text-slate-300 hover:text-red-500" data-testid={`inv-li-remove-${invoice.id}-${idx}`}><Trash2 className="w-3 h-3" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {local.status === "draft" && (
            <Button type="button" variant="outline" size="sm" className="h-6 text-[10px]" onClick={addBlankItem} data-testid={`inv-add-line-${invoice.id}`}>
              <Plus className="w-3 h-3 mr-1" /> Add Line
            </Button>
          )}

          {/* Totals */}
          <TaxOverrideInline open={taxDialogOpen} onOpenChange={setTaxDialogOpen} docType="invoice" docId={invoice.id}
            current={{ tax_rate: local.tax_override?.type === "rate" ? local.tax_override.prior_rate : local.tax_rate, tax_authority: local.tax_authority, taxable_base: local.taxable_subtotal ?? recalcTotals(local.line_items || [], local.tax_rate, null, null).taxable_subtotal, tax_amount: recalcTotals(local.line_items || [], local.tax_override?.type === "rate" ? local.tax_override.prior_rate : local.tax_rate, null, null).tax_amount, tax_override: local.tax_override }}
            onApplied={applyTaxOverrideResult} />
          <div className="flex justify-end">
            <div className="w-56 space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(local.subtotal)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Tax</span>
                <div className="flex items-center gap-2">
                  <span className={`text-slate-500 ${local.tax_override ? "text-amber-700 font-semibold" : ""}`} data-testid={`inv-tax-rate-${invoice.id}`}>
                    {local.tax_override?.type === "amount" ? "manual" : `${local.tax_rate || 0}%`}
                  </span>
                  {local.tax_rate_manual && !local.tax_override && (
                    <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800" title="Manual rate carried from the work order" data-testid={`inv-tax-manual-${invoice.id}`}>MANUAL</span>
                  )}
                  <span className={`ml-1 tabular-nums ${local.tax_override ? "text-amber-700 font-semibold" : ""}`} data-testid={`inv-tax-amount-${invoice.id}`}>{fmt(local.tax_amount)}</span>
                </div>
              </div>
              {local.status === "draft" && canOverrideTax && (
                <div className="flex justify-end">
                  <button type="button" className="text-[10px] text-amber-700 underline hover:text-amber-900" onClick={() => setTaxDialogOpen(true)} data-testid={`inv-tax-override-btn-${invoice.id}`}>
                    {local.tax_override ? "Change tax" : "Override tax"}
                  </button>
                </div>
              )}
              {local.tax_override && (
                <TaxOverrideBadge override={local.tax_override} stale={!!local.tax_override_stale} docType="invoice" docId={invoice.id}
                  canManage={canOverrideTax && local.status === "draft"} onChange={applyTaxOverrideResult} testId={`inv-tax-override-badge-${invoice.id}`} />
              )}
              {!local.tax_override && local.tax_amount_override != null && (
                <div className="text-[10px] text-amber-700" data-testid={`inv-tax-override-notice-${invoice.id}`}>Legacy manual tax amount — use Override tax to change or clear</div>
              )}
              {local.tax_override_notice && (
                <div className="text-[10px] text-red-700 leading-tight" data-testid={`inv-tax-review-notice-${invoice.id}`}>{local.tax_override_notice}</div>
              )}
              <div className="flex justify-between font-bold text-sm border-t pt-1"><span>Total</span><span className="tabular-nums">{fmt(local.total)}</span></div>
            </div>
          </div>

          {/* Material / Labor breakdown toggle — when on, the generated PDF
              adds Material + Labor subtotals above Subtotal. Off by default. */}
          {local.status === "draft" && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid={`inv-show-breakdown-${invoice.id}`}>
              <input
                type="checkbox"
                checked={!!local.show_breakdown}
                onChange={e => updateField("show_breakdown", e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300"
              />
              <span>Show Material & Labor breakdown on PDF</span>
            </label>
          )}

          {/* Customer PO # — inherited from the WO, editable per-invoice */}
          {local.status === "draft" ? (
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-[10px] text-slate-500 font-medium">Customer PO#</label>
                <Input value={local.customer_po_number || ""} maxLength={15} onChange={(e) => updateField("customer_po_number", e.target.value.slice(0, 15))} className="h-7 w-40 text-xs" placeholder="Customer PO #" data-testid="inv-customer-po" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-medium">Invoice date <span className="text-slate-400">(Foundation invoice_date)</span></label>
                <Input type="date" value={(local.invoice_date || local.created_at || "").slice(0, 10)} onChange={(e) => updateField("invoice_date", e.target.value)} className="h-7 w-40 text-xs" data-testid="inv-invoice-date" />
              </div>
            </div>
          ) : local.customer_po_number ? (
            <div className="text-xs text-slate-600 bg-slate-50 rounded p-2" data-testid="inv-customer-po-readonly"><span className="font-semibold">Customer PO#:</span> {local.customer_po_number}</div>
          ) : null}

          {/* Notes */}
          {local.status === "draft" ? (
            <div>
              <label className="text-[10px] text-slate-500 font-medium">Notes</label>
              <Textarea value={notesToText(local.notes)} onChange={(e) => updateField("notes", e.target.value)} rows={2} className="text-xs" placeholder="Invoice notes..." data-testid="inv-notes" />
            </div>
          ) : local.notes ? (
            <div className="text-xs text-slate-600 bg-slate-50 rounded p-2 whitespace-pre-wrap"><span className="font-semibold">Notes:</span> {notesToText(local.notes)}</div>
          ) : null}

          {/* Reminder Info */}
          {local.status !== "paid" && local.status !== "draft" && (
            <div className="space-y-2" data-testid={`invoice-reminders-${invoice.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-semibold text-slate-700">Reminders</span>
                  {local.last_reminder_tier > 0 && (
                    <span className="text-[10px] text-slate-400">Last: Tier {local.last_reminder_tier} {local.last_reminder_at ? `(${shortDate(local.last_reminder_at)})` : ""}</span>
                  )}
                  {local.reminders_paused && <Badge className="text-[10px] bg-yellow-100 text-yellow-700">Paused</Badge>}
                </div>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="outline" size="sm" className="h-6 text-[10px]" onClick={handleTogglePause} data-testid={`btn-toggle-pause-${invoice.id}`}>
                    {local.reminders_paused ? <><Bell className="w-3 h-3 mr-1" /> Resume</> : <><BellOff className="w-3 h-3 mr-1" /> Pause</>}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] text-amber-600 border-amber-200 hover:bg-amber-50" onClick={handleSendReminder} data-testid={`btn-send-reminder-${invoice.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Send Reminder
                  </Button>
                </div>
              </div>
              {reminderLog.length > 0 && (
                <div className="text-[10px] space-y-1 max-h-[80px] overflow-y-auto">
                  {reminderLog.slice(0, 5).map(r => (
                    <div key={r.id} className="flex items-center gap-2 text-slate-500">
                      <span className="text-slate-400 num">{shortDate(r.sent_at)}</span>
                      <span className="font-medium">{r.tier_label}</span>
                      <span>{r.recipient_email}</span>
                      <Badge className={`text-[8px] ${
                        r.status === "sent" ? "bg-emerald-100 text-emerald-700" :
                        r.status === "skipped" ? "bg-amber-100 text-amber-700" :
                        r.status === "error" ? "bg-rose-100 text-rose-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>{r.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Resend delivery audit (delivered / bounced / etc.) */}
          <EmailHistoryPanel refType="invoice" refId={invoice.id} />

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-1 border-t">
            <div className="flex items-center gap-2">
              {local.status === "draft" && (
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleDelete} data-testid="btn-delete-invoice">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
              {local.status === "sent" && (
                <Button type="button" size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleStatusChange("paid")} data-testid="btn-mark-paid">
                  <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                </Button>
              )}
              {local.status === "paid" && (
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleStatusChange("sent")} data-testid="btn-undo-paid">
                  Undo paid
                </Button>
              )}
              {payOn && local.status !== "paid" && local.status !== "draft" && (
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs text-teal-600 border-teal-200 hover:bg-teal-50" onClick={async () => {
                  try {
                    const res = await createPaymentLink(invoice.id);
                    const url = res.data?.payment_url;
                    if (url) {
                      setLocal(prev => ({ ...prev, square_payment_url: url }));
                      window.open(url, "_blank");
                      toast.success("Payment link created — opened in new tab");
                    }
                  } catch (err) { toast.error("Failed to create payment link"); }
                }} data-testid={`btn-pay-now-${invoice.id}`}>
                  <CreditCard className="w-3 h-3 mr-1" /> Pay Now Link
                </Button>
              )}
              {local.square_payment_url && (
                <a href={local.square_payment_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-teal-600 hover:underline" data-testid={`pay-link-${invoice.id}`}>
                  <ExternalLink className="w-3 h-3" /> Payment Link
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              {dirty && (
                <Button type="button" size="sm" className={btn("write")} onClick={handleSave} disabled={saving} data-testid="btn-save-invoice-changes">
                  {saving ? "Saving..." : "Save changes"}
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowPreview(true)} data-testid={`btn-view-invoice-${invoice.id}`}>
                <Eye className="w-3 h-3 mr-1" /> View
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={generatePDF} data-testid={`btn-pdf-invoice-${invoice.id}`}>
                <FileDown className="w-3 h-3 mr-1" /> PDF
              </Button>
              <Button type="button" size="sm" className="h-7 text-xs" onClick={handleEmail} data-testid={`btn-email-invoice-${invoice.id}`}>
                <Mail className="w-3 h-3 mr-1" /> Invoice
              </Button>
              {/* Phase 3 — spec-compliant AR push (ar_invoice + ar_invoice_gl).
                  Preview every column + gate, then confirm. Replaces the
                  header-only push-to-foundation path. */}
              {(local.status === "sent" || local.status === "paid" || local.status === "partial") && (
                <FoundationARPushButton
                  invoice={local}
                  testIdSuffix={`inv-${invoice.id}`}
                  onPushed={(res) => {
                    setLocal((prev) => ({
                      ...prev,
                      foundation_ar_status: res.outcome,
                      foundation_ar_invoice_no: (res.invoice_no_sent || "").trim(),
                    }));
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <InvoicePreviewModal
          invoice={local}
          clientName={clientName}
          woNumber={woNumber}
          jobsiteAddress={jobsiteAddress}
          onSaved={onSaved}
          onClose={() => setShowPreview(false)}
        />
      )}

    </div>
  );
}

/* ─── Invoice Preview Modal ─── */
function InvoicePreviewModal({ invoice, clientName, woNumber, jobsiteAddress, onSaved, onClose }) {
  const ci = COMPANY_INFO;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose} data-testid="invoice-preview-modal">
      <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[90vh] overflow-auto p-8 relative" onClick={e => e.stopPropagation()}>
        <button className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" onClick={onClose} data-testid="invoice-preview-close"><X className="w-5 h-5" /></button>

        {/* Company header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-bold text-sm text-slate-800">{ci.name}</div>
            <div className="text-[11px] text-slate-500">{ci.fullAddress}</div>
            <div className="text-[11px] text-slate-500">{ci.phone} | {ci.email}</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-slate-800 tracking-wide">INVOICE</div>
          </div>
        </div>

        {/* Invoice info */}
        <div className="grid grid-cols-2 gap-4 text-xs text-slate-700 mb-4 border-t pt-3">
          <div className="space-y-1">
            <div><span className="font-semibold">Invoice #:</span> {invoice.invoice_number}</div>
            <div><span className="font-semibold">Date:</span> {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : "—"}</div>
            <div><span className="font-semibold">WO #:</span> {invoice.wo_number || woNumber || "—"}</div>
            {invoice.project_manager_name && <div><span className="font-semibold">PM:</span> {invoice.project_manager_name}</div>}
          </div>
          <div className="space-y-1 text-right">
            <div>
              <Badge className={`text-[10px] ${STATUS_COLORS[invoice.status] || STATUS_COLORS.draft}`}>
                {STATUS_LABELS[invoice.status] || invoice.status}
              </Badge>
            </div>
            {invoice.sent_at && <div className="text-[10px] text-slate-400">Sent: {new Date(invoice.sent_at).toLocaleDateString()}</div>}
            {invoice.sent_at && invoice.status !== "paid" && (
              <div className="flex items-center justify-end gap-1 text-[10px] text-slate-400">
                <span>Due:</span>
                <input type="date" value={(invoice.due_date || "").slice(0, 10)} data-testid={`invoice-due-date-${invoice.id}`}
                  title="Defaults to 45 days after sending — change it if the customer has other terms"
                  onChange={async (e) => { try { await updateInvoice(invoice.id, { due_date: e.target.value || null }); onSaved?.(); } catch { toast.error("Could not update due date"); } }}
                  className="h-5 rounded border border-transparent bg-transparent px-1 text-[10px] text-slate-600 hover:border-slate-300 focus:border-brand focus:outline-none" />
              </div>
            )}
            {invoice.paid_at && <div className="text-[10px] text-emerald-600 font-medium">Paid: {new Date(invoice.paid_at).toLocaleDateString()}</div>}
          </div>
        </div>

        {/* Bill To & Project */}
        <div className="grid grid-cols-2 gap-6 text-xs mb-4 border-t pt-3">
          <div>
            <div className="font-bold text-slate-600 uppercase text-[10px] mb-1">Bill To</div>
            <div className="font-medium text-slate-800">{invoice.customer_name || clientName || "—"}</div>
            {invoice.customer_contact && <div className="text-slate-500">{invoice.customer_contact}</div>}
            {invoice.customer_email && <div className="text-slate-500">{invoice.customer_email}</div>}
            {invoice.customer_phone && <div className="text-slate-500">{invoice.customer_phone}</div>}
          </div>
          <div>
            <div className="font-bold text-slate-600 uppercase text-[10px] mb-1">Project</div>
            <div className="text-slate-700">{invoice.project_address || jobsiteAddress || "—"}</div>
          </div>
        </div>

        {/* Line Items */}
        <table className="w-full text-xs border-collapse mb-4">
          <thead>
            <tr className="bg-slate-700 text-white text-[10px] uppercase">
              <th className="py-1.5 px-2 text-left w-8">#</th>
              <th className="py-1.5 px-2 text-left">Description</th>
              <th className="py-1.5 px-2 text-left w-20">Part #</th>
              <th className="py-1.5 px-2 text-center w-12">Qty</th>
              <th className="py-1.5 px-2 text-right w-24">Unit Price</th>
              <th className="py-1.5 px-2 text-right w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.line_items || []).map((li, idx) => (
              <tr key={li.id || idx} className="border-b border-slate-100">
                <td className="py-1.5 px-2 text-slate-400">{idx + 1}</td>
                <td className="py-1.5 px-2 font-medium text-slate-700">{li.description}</td>
                <td className="py-1.5 px-2 text-slate-500">{li.part_number || ""}</td>
                <td className="py-1.5 px-2 text-center tabular-nums">{li.quantity}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">{fmt(li.unit_price)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{fmt(li.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="text-xs space-y-1 min-w-[220px]">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tax ({invoice.tax_rate || 0}%)</span><span className="tabular-nums">{fmt(invoice.tax_amount)}</span></div>
            {invoice.hide_tax && (
              <div className="flex justify-end pt-0.5">
                <TaxHiddenBadge taxAmount={invoice.tax_amount} taxRate={invoice.tax_rate} testId={`invoice-tax-hidden-badge-${invoice.id}`} />
              </div>
            )}
            <div className="flex justify-between font-bold text-sm border-t pt-1"><span>TOTAL DUE</span><span className="tabular-nums">{fmt(invoice.total)}</span></div>
          </div>
        </div>

        {invoice.customer_po_number && (
          <div className="mt-4 border-t pt-3">
            <div className="text-[10px] font-bold text-slate-600 uppercase mb-1">Customer PO#</div>
            <div className="text-xs text-slate-600">{invoice.customer_po_number}</div>
          </div>
        )}

        {invoice.notes && (
          <div className="mt-4 border-t pt-3">
            <div className="text-[10px] font-bold text-slate-600 uppercase mb-1">Notes</div>
            <div className="text-xs text-slate-600 whitespace-pre-wrap">
              {notesToText(invoice.notes)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* ─── Foundation Export Modal ─── */
